import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { capsSx } from "@/pages/sawlines/components/primitives";
import {
	matchState,
	WorkOrderMatchChip,
} from "@/pages/sawlines/components/WorkOrderMatch";
import { formatEntryLength } from "@/pages/sawlines/utils/entryBarcode";
import type { SawEntryLoaded } from "../repositories/sawEntryQueueRepository";
import { materialText } from "./MaterialQueueRow";

// time | tag, material, part, loaded by | WO + match | Unload
const COLUMNS = "44px minmax(0, 1fr) 118px 56px";

const eventSx = {
	display: "inline-flex",
	alignItems: "center",
	gap: 0.5,
	fontSize: 13,
	whiteSpace: "nowrap",
} as const;

const LoadedRow = ({
	row,
	busy,
	onUnload,
}: {
	row: SawEntryLoaded;
	busy: boolean;
	onUnload: () => void;
}) => {
	const { tint } = matchState(row.LoadedMatch);
	return (
		<>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: COLUMNS,
					columnGap: 1.25,
					alignItems: "start",
					py: 1,
					pl: 2,
					pr: 1,
					borderBottom: 1,
					borderColor: "divider",
					...(tint && {
						bgcolor: tint.bg,
						boxShadow: `inset 6px 0 0 ${tint.bar}`,
					}),
				}}
			>
				<Typography
					color="text.secondary"
					sx={{ fontSize: 15, fontVariantNumeric: "tabular-nums" }}
				>
					{dxtToLocalServerTime(row.LoadedAt, "HH:mm")}
				</Typography>
				<Box sx={{ minWidth: 0, color: "text.secondary", fontSize: 13 }}>
					<Typography
						sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}
					>
						{row.TagNo}
					</Typography>
					<div>
						{materialText(
							row.SectionName,
							row.GradeName,
							formatEntryLength(
								row.MillLengthIn,
								row.ScannedPartNumber != null,
							),
						)}
					</div>
					{row.ScannedPartNumber && <div>Part {row.ScannedPartNumber}</div>}
					{row.LoadedByEmployee && (
						<Box sx={{ fontSize: 12 }}>Loaded by #{row.LoadedByEmployee}</Box>
					)}
				</Box>
				<Box>
					<Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.5 }}>
						{row.LoadedWorkOrderNumber ?? "No WO"}
					</Typography>
					<WorkOrderMatchChip match={row.LoadedMatch} />
				</Box>
				<Button
					variant="contained"
					color="error"
					disabled={busy}
					aria-label={`Unload tag ${row.TagNo}`}
					onClick={onUnload}
					sx={{
						minWidth: 0,
						alignSelf: "stretch",
						p: 0,
						flexDirection: "column",
						gap: 0.25,
						fontSize: 11,
						lineHeight: 1,
					}}
				>
					<UndoRoundedIcon />
					Unload
				</Button>
			</Box>
			{(row.StartedWorkOrderNumber || row.CompletedWorkOrderNumber) && (
				<Stack
					direction="row"
					gap={1.75}
					flexWrap="wrap"
					sx={{
						py: 0.75,
						pl: 8.75,
						pr: 1,
						bgcolor: "#eef3ee",
						borderBottom: 1,
						borderColor: "divider",
					}}
				>
					{row.StartedWorkOrderNumber && (
						<Box sx={{ ...eventSx, color: "success.dark", fontWeight: 700 }}>
							<PlayCircleOutlineIcon fontSize="inherit" />
							Started {row.StartedWorkOrderNumber}
						</Box>
					)}
					{row.CompletedWorkOrderNumber && (
						<Box sx={{ ...eventSx, color: "text.secondary" }}>
							<CheckCircleOutlineIcon fontSize="inherit" />
							Completed {row.CompletedWorkOrderNumber}
						</Box>
					)}
				</Stack>
			)}
		</>
	);
};

export const LoadedThisShift = ({
	loaded,
	busy,
	onUnload,
}: {
	loaded?: SawEntryLoaded[];
	busy: boolean;
	onUnload: (row: SawEntryLoaded) => void;
}) => (
	<Paper
		sx={{
			// stacked: 3:2 with Staged, each scrolls inside
			flex: { xs: 2, md: "0 0 35%" },
			display: "flex",
			flexDirection: "column",
			minHeight: 0,
			overflow: "hidden",
		}}
	>
		<Stack
			direction="row"
			alignItems="center"
			gap={1.25}
			sx={{ p: 1.5, px: 2, borderBottom: 1, borderColor: "divider" }}
		>
			<Typography component="h2" variant="h6">
				Loaded this shift
			</Typography>
			{loaded && (
				<Chip
					label={loaded.length}
					variant="outlined"
					sx={{ fontSize: 16, fontWeight: 700, height: 34 }}
				/>
			)}
		</Stack>
		<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
			{loaded && loaded.length > 0 && (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: COLUMNS,
						columnGap: 1.25,
						py: 1.1,
						pl: 2,
						pr: 1,
						bgcolor: "#fafbfc",
						borderBottom: 2,
						borderColor: "divider",
						"& > span": capsSx,
					}}
				>
					<span>Time</span>
					<span>Tag</span>
					<span>Work order</span>
				</Box>
			)}
			{loaded?.map((row) => (
				<LoadedRow
					key={row.EntryBundleId}
					row={row}
					busy={busy}
					onUnload={() => onUnload(row)}
				/>
			))}
			{loaded?.length === 0 && (
				<Typography color="text.secondary" sx={{ p: 2 }}>
					Nothing loaded yet this shift.
				</Typography>
			)}
		</Box>
	</Paper>
);
